/**
 * giftManager — Sprint 3 §3c · "Regalar conocimiento"
 * Turn a spot or a micro-itinerary into a self-contained, offline gift link:
 *   <app-origin><path>?gift=<base64url(JSON)>
 * No backend, no account — the whole payload travels inside the URL. If it
 * doesn't fit inline (> GIFT_INLINE_MAX_BYTES), we CAP it (ask to trim) rather
 * than fall back to a server short-code (confirmed: fully offline).
 *
 * The receiver opens the link/QR → deepLinkManager decodes it → it lands in a
 * private "received gifts" inbox (localStorage, per-owner). Attribution is
 * carried only if the giver opted in.
 */

export const GIFT_SCHEME = 'wayfare://gift';   // canonical name; web links use ?gift=
export const GIFT_PARAM = 'gift';
export const GIFT_INLINE_MAX_BYTES = 1800;

const INBOX_KEY_BASE = 'wayfare_gifts_inbox';

// ── utf8-safe base64url ─────────────────────────────────────────────────────
const b64urlEncode = (str) => {
  const b64 = btoa(unescape(encodeURIComponent(str)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const b64urlDecode = (enc) => {
  const b64 = enc.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(b64)));
};

const owner = () => {
  try { return localStorage.getItem('wayfare_owner') || '_'; } catch { return '_'; }
};
const inboxKey = () => `${INBOX_KEY_BASE}_${owner()}`;

/**
 * Encode a gift payload into a shareable URL.
 * @param {{type:'spot'|'itinerary', data:object, from?:string|null}} gift
 * @returns {{ ok:true, url:string, encoded:string } | { ok:false, reason:'empty'|'too_big', size?:number }}
 */
export function encodeGift(gift) {
  if (!gift || !gift.type || !gift.data) return { ok: false, reason: 'empty' };
  const payload = { v: 1, type: gift.type, data: gift.data, from: gift.from || null };
  const encoded = b64urlEncode(JSON.stringify(payload));
  if (encoded.length > GIFT_INLINE_MAX_BYTES) {
    return { ok: false, reason: 'too_big', size: encoded.length };
  }
  const base = `${window.location.origin}${window.location.pathname}`;
  return { ok: true, url: `${base}?${GIFT_PARAM}=${encoded}`, encoded };
}

/** Decode an encoded gift string back to its payload (or null if invalid). */
export function decodeGift(encoded) {
  try {
    const payload = JSON.parse(b64urlDecode(encoded));
    if (!payload || !payload.type || !payload.data) return null;
    return payload;
  } catch { return null; }
}

// ── Inbox (localStorage, per-owner) ─────────────────────────────────────────

export function getReceivedGifts() {
  try { return JSON.parse(localStorage.getItem(inboxKey()) || '[]'); } catch { return []; }
}

/** Store a received gift; de-dupes by encoded signature. Returns the stored item. */
export function addReceivedGift(payload, encoded) {
  const gifts = getReceivedGifts();
  const sig = encoded || JSON.stringify(payload);
  if (gifts.some(g => g.sig === sig)) return null; // already have it
  const item = {
    id: `gift_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    sig,
    type: payload.type,
    data: payload.data,
    from: payload.from || null,
    receivedAt: new Date().toISOString(),
  };
  gifts.unshift(item);
  try { localStorage.setItem(inboxKey(), JSON.stringify(gifts.slice(0, 100))); } catch { /* full */ }
  return item;
}

export function removeReceivedGift(id) {
  const gifts = getReceivedGifts().filter(g => g.id !== id);
  try { localStorage.setItem(inboxKey(), JSON.stringify(gifts)); } catch { /* ignore */ }
  return gifts;
}
